import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Size, noteReplyProblem, noteThreadToggleLabel, t } from '@bms2/shared';
import { Button, Textarea } from '../controls';
import { Icon } from '../Icon';
import styles from './NoteThread.module.css';

/** A reply as the thread draws it: the time already written in the viewer's own zone. */
export interface NoteThreadReply {
  id: string;
  author: string;
  when: string;
  body: string;
  mayDelete: boolean;
}

export interface NoteThreadProps {
  noteId: string;
  /** Oldest first, the order they were written. */
  replies: readonly NoteThreadReply[];
  /** Sends a reply; answers the server's refusal in words, or null once it has saved. */
  onReply: (body: string) => Promise<string | null>;
  /** Deletes a reply; answers the refusal in words, or null once it has gone. */
  onDelete: (replyId: string) => Promise<string | null>;
  /**
   * Whether the thread is open, held by the page where it passes one, so a reply
   * that reads the notes again does not fold the thread it was written in.
   */
  open?: boolean;
  onToggle?: () => void;
  /** What stands at the left of the note's foot line, before the replies glyph: the note's Show more where its text is cut (decision 5493). */
  leading?: ReactNode;
  /**
   * Where the replies glyph stands when the note's text is not cut: a span at the end of the
   * text's own last line, which the glyph is drawn into so it sits at that line's right end
   * where the line has room and wraps to the next line where it does not (T2904).
   */
  inlineSlot?: HTMLElement | null;
}

/**
 * 🔒 The replies beneath a note (piece 13; decision 0671), drawn inside the Note
 * item so every Internal Notes tile and every document page's panel carry them.
 * 🔒 The note's foot is one line (decision 5493): Show more at the left where the
 * text is cut and a small replies glyph with the count at the right, as X and
 * Facebook draw it. Opened, the replies read oldest first with their author and
 * time, and the Reply box sits beneath them; a folded note offers no Reply.
 * 🔨 A reply is plain text through the Textarea, and Delete is drawn only on a
 * reply the server would let this viewer delete.
 */
export function NoteThread({ noteId, replies, onReply, onDelete, open: heldOpen, onToggle, leading = null, inlineSlot = null }: NoteThreadProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = heldOpen ?? ownOpen;
  const toggle = onToggle ?? (() => setOwnOpen((was) => !was));
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const problem = noteReplyProblem(draft);
    if (problem) return setError(problem);
    setBusy(true);
    const refused = await onReply(draft.trim());
    setBusy(false);
    setError(refused);
    if (!refused) setDraft('');
  };
  const remove = async (replyId: string) => {
    setBusy(true);
    setError(await onDelete(replyId));
    setBusy(false);
  };
  const bubble = (
    <button type="button" className={`${styles.toggle} ${inlineSlot ? styles.toggleInline : ''}`} aria-expanded={open} aria-label={noteThreadToggleLabel(replies.length, open)} title={noteThreadToggleLabel(replies.length, open)} onClick={toggle} data-note-thread-toggle={noteId}>
      <Icon meaning="comment" size="inline" />
      {replies.length > 0 ? <span data-note-reply-total={replies.length}>{replies.length}</span> : null}
    </button>
  );
  return (
    <div className={styles.thread} data-note-thread={noteId} data-note-thread-open={open ? 'true' : 'false'} data-note-reply-count={replies.length} data-note-bubble={inlineSlot ? 'inline' : 'foot'}>
      {inlineSlot ? (
        createPortal(bubble, inlineSlot)
      ) : (
        <div className={styles.foot} data-note-foot="true">
          {leading}
          {bubble}
        </div>
      )}
      {open ? (
        <>
          {replies.length > 0 ? (
            <ol className={styles.replies}>
              {replies.map((reply) => (
                <li key={reply.id} className={styles.reply} data-note-reply={reply.id}>
                  <span className={styles.replyHead}>
                    <span className={styles.replyAuthor}>{reply.author}</span>
                    <span className={styles.replyWhen}>{reply.when}</span>
                    {reply.mayDelete ? (
                      <button type="button" className={styles.remove} aria-label={t('note.reply.delete')} disabled={busy} onClick={() => void remove(reply.id)} data-note-reply-delete={reply.id}>
                        <Icon meaning="remove" size="inline" />
                      </button>
                    ) : null}
                  </span>
                  <span className={styles.replyBody}>{reply.body}</span>
                </li>
              ))}
            </ol>
          ) : null}
          <div className={styles.compose}>
            <Textarea size={Size.Large} label={t('note.reply.label')} labelHidden value={draft} onChange={(next) => { setDraft(next); setError(null); }} name={`reply-${noteId}`} placeholder={t('note.reply.label')} error={error} />
            <Button size={Size.Small} text={[t('note.reply.send')]} disabled={busy} actions={[() => void send()]} />
          </div>
        </>
      ) : null}
    </div>
  );
}
